import React, { useState, useEffect, useMemo, useCallback } from "react";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";

import CommonLayout from "../../Common/Layout";
import { setPrevState } from "../../../util/common";
import SnowflakeService from "../../../service/snowflake";
import Toolbar from "../../Common/Toolbar";

const useStyles = makeStyles()((theme) => ({
  PanelContainer: {
    padding: "10px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
      wordBreak: "break-word",
    },
    "& .MuiTablePagination-toolbar": {
      minHeight: "40px",
    },
    "& P.MuiTablePagination-selectLabel,P.MuiTablePagination-displayedRows": {
      marginBottom: "0px !important",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "60px",
      marginLeft: "-2px",
    },
  },
  stickyCell: {
    position: "sticky",
    left: "0px",
    zIndex: 800,
    backgroundColor: "white",
  },
}));

const SnowflakeUsage = () => {
  const { classes } = useStyles();

  const [usageReportState, setUsageReportState] = useState({
    loading: false,
    data: {},
  });

  const { data, loading } = usageReportState;
  const dataLength = useMemo(() => Object.keys(data).length, [data]);

  // Render table content based on data availability
  const RenderTableContent = useCallback(() => {
    if (dataLength !== 0) {
      return (
        <Paper
          sx={{ width: "100%", overflow: "hidden" }}
          className={classes.DataTableContainer}
          style={{ position: "relative" }}
        >
          <TableContainer sx={{ maxHeight: 270 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.stickyCell} align="left" style={{ minWidth: 120, fontWeight: 750 }}>
                    Month
                  </TableCell>
                  {Object.keys(data).map((month, index) => (
                    <TableCell key={month} align="center" style={{ minWidth: 120, fontWeight: 750 }}>
                      {index === dataLength - 2 ? (
                        <>
                          {month} <br></br>(Current)
                        </>
                      ) : index === dataLength - 1 ? (
                        <>
                          {month.split(" Projected")[0]} <br></br>(Projected)
                        </>
                      ) : (
                        <>{month}</>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow tabIndex={-1}>
                  <TableCell align={"left"} className={classes.stickyCell}>
                    Compute (Credits Used)
                  </TableCell>
                  {Object.keys(data).map((month, index) => (
                    <TableCell
                      align={"center"}
                      key={index}
                      sx={{ color: index === dataLength - 1 && "rgba(0,0,0,0.50)" }}
                    >
                      {data[month][0]}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow tabIndex={-1}>
                  <TableCell align={"left"} className={classes.stickyCell}>
                    Average Storage Used (GB)
                  </TableCell>
                  {Object.keys(data).map((month, index) => (
                    <TableCell
                      align={"center"}
                      key={index}
                      sx={{ color: index === dataLength - 1 && "rgba(0,0,0,0.50)" }}
                    >
                      {data[month][1]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      );
    } else {
      return (
        <Paper
          sx={{ width: "100%", overflow: "hidden" }}
          className={classes.DataTableContainer}
          style={{ position: "relative" }}
        >
          <Table stickyHeader aria-label="sticky table">
            <TableBody>
              <TableRow>
                <TableCell align={"center"} key={0}>
                  No data available for Snowflake Usage Report
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      );
    }
  }, [classes.DataTableContainer, classes.stickyCell, data, dataLength]);

  useEffect(() => {
    const getUsageReport = async () => {
      setPrevState(setUsageReportState, { loading: true });
      const sfService = new SnowflakeService();
      const response = await sfService.getUsageReport();
      if (response.success) {
        const { data } = response;
        setPrevState(setUsageReportState, { data });
      }
      setPrevState(setUsageReportState, { loading: false });
    };

    getUsageReport();

    return () => {};
  }, []);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/reports", displayName: "Reports", active: false },
      { link: "#", displayName: "Snowflake Usage", active: true },
    ],
    []
  );

  return (
    <CommonLayout title="Snowflake Usage" breadcrumbArr={breadcrumbArr}>
      <Container maxWidth="false" sx={{ m: 0 }} className={classes.PanelContainer}>
        <Toolbar title="Snowflake Usage" helpText="Snowflake Usage" />

        {loading ? (
          <Box style={{ position: "relative", width: "100%", minHeight: 135 }}>
            <Backdrop
              sx={{ backgroundColor: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              style={{ minHeight: "100%", width: "100%", position: "absolute", borderRadius: 10 }}
              open={loading}
            >
              <CircularProgress color="primary" />
            </Backdrop>
          </Box>
        ) : (
          <RenderTableContent />
        )}
      </Container>
    </CommonLayout>
  );
};

export default React.memo(SnowflakeUsage);
